export enum DashboardWidgetType {
  CA = 'ca',
  NOTICEBOARD = 'noticeboard',
  TEAM_CHAT = 'team-chat',
  UPCOMING_EVENTS = 'upcoming-events',
  TASKS = 'tasks',
  CIM_ANALYTICS = 'cim-analytics',
  BUSINESS_TIMELINE = 'business-timeline',
  PIPELINE_STATUS = 'pipeline-status',
  DOC_STATUS_SUMMARY = 'doc-status-summary',
  DOC_STATUS_CHART = 'doc-status-chart',
  NEW_BUSINESSES_MONTH = 'new-businesses-month',
  SOLD_BUSINESSES_MONTH = 'sold-businesses-month',
  BUSINESSES_BY_AGENT = 'businesses-by-agent',
  COMMISSION_SUMMARY = 'commission-summary',
  COMMISSION_BY_AGENT = 'commission-by-agent',
  COMMISSION_BY_MONTH = 'commission-by-month',
  COMMISSION_BY_STAGE = 'commission-by-stage',
  COMMISSION_BY_STATUS = 'commission-by-status',
  OFFICE_VS_AGENT = 'office-vs-agent',
  INDUSTRY_SUMMARY = 'industry-summary',
  TOTAL_BUSINESSES_CHART = 'total-businesses-chart',
  TOTAL_PRICE_CHART = 'total-price-chart',
  AVERAGE_PRICE_CHART = 'average-price-chart',
  AVERAGE_MULTIPLE_CHART = 'average-multiple-chart',
  VDR_REQUESTS = 'vdr-requests',
}

export interface DashboardWidget {
  id: string
  name: string
  enabled: boolean
  order: number
  minsize: 1 | 2
}

export interface DashboardRow {
  widgets: string[]
  size: 1 | 2
}

export const WIDGET_COMPONENT_MAP: Record<string, string> = {
  'CA\'s Requiring Qualification': 'DashboardCA',
  'Admin Noticeboard': 'DashboardAdminNoticeboard',
  'Team Chat': 'DashboardTeamChat',
  'Upcoming Events': 'DashboardUpcomingEvents',
  'My Tasks': 'DashboardTasks',
  'CIM Analytics': 'DashboardCIMAnalytic',
  'Business Timeline': 'DashboardBusinessTimeline',
  'Pipeline Status Chart': 'DashboardBusinessAnalyticsPipelineStatusChart',
  'Document Status Summary': 'DashboardBusinessAnalyticsDocumentStatusSummary',
  'Document Status Chart': 'DashboardBusinessAnalyticsDocumentStatusChart',
  'New Businesses by Month': 'DashboardBusinessAnalyticsNewBusinessesByMonthChart',
  'Sold Businesses by Month': 'DashboardBusinessAnalyticsSoldBusinessesByMonthChart',
  'Businesses by Agent': 'DashboardBusinessAnalyticsBusinessesByAgentChart',
  'Commission Summary': 'DashboardCommissionAnalyticsSummary',
  'Commission by Agent': 'DashboardCommissionAnalyticsCommissionByAgentChart',
  'Commission by Month': 'DashboardCommissionAnalyticsCommissionByMonthChart',
  'Commission by Stage': 'DashboardCommissionAnalyticsCommissionByStageChart',
  'Commission by Status': 'DashboardCommissionAnalyticsCommissionByStatusChart',
  'Office vs Agent Breakdown': 'DashboardCommissionAnalyticsOfficeVsAgentBreakdown',
  'Industry Summary': 'DashboardIndustryAnalyticsSummary',
  'Total Businesses Chart': 'DashboardIndustryAnalyticsTotalBusinessesChart',
  'Total Price Chart': 'DashboardIndustryAnalyticsTotalPriceChart',
  'Average Price Chart': 'DashboardIndustryAnalyticsAveragePriceChart',
  'Average Multiple Chart': 'DashboardIndustryAnalyticsAverageMultipleChart',
}

const getDefaultWidgets = (): DashboardWidget[] => [
  { id: DashboardWidgetType.CA, name: 'CA\'s Requiring Qualification', enabled: true, order: 1, minsize: 1 },
  { id: DashboardWidgetType.NOTICEBOARD, name: 'Admin Noticeboard', enabled: true, order: 2, minsize: 1 },
  { id: DashboardWidgetType.TEAM_CHAT, name: 'Team Chat', enabled: true, order: 3, minsize: 1 },
  { id: DashboardWidgetType.UPCOMING_EVENTS, name: 'Upcoming Events', enabled: true, order: 4, minsize: 1 },
  { id: DashboardWidgetType.TASKS, name: 'My Tasks', enabled: true, order: 5, minsize: 1 },
  { id: DashboardWidgetType.CIM_ANALYTICS, name: 'CIM Analytics', enabled: true, order: 6, minsize: 2 },
  { id: DashboardWidgetType.BUSINESS_TIMELINE, name: 'Business Timeline', enabled: true, order: 7, minsize: 2 },
  { id: DashboardWidgetType.PIPELINE_STATUS, name: 'Pipeline Status Chart', enabled: true, order: 8, minsize: 2 },
  { id: DashboardWidgetType.DOC_STATUS_SUMMARY, name: 'Document Status Summary', enabled: true, order: 9, minsize: 1 },
  { id: DashboardWidgetType.DOC_STATUS_CHART, name: 'Document Status Chart', enabled: true, order: 10, minsize: 2 },
  { id: DashboardWidgetType.NEW_BUSINESSES_MONTH, name: 'New Businesses by Month', enabled: true, order: 11, minsize: 2 },
  { id: DashboardWidgetType.SOLD_BUSINESSES_MONTH, name: 'Sold Businesses by Month', enabled: false, order: 12, minsize: 2 },
  { id: DashboardWidgetType.BUSINESSES_BY_AGENT, name: 'Businesses by Agent', enabled: true, order: 13, minsize: 2 },
  { id: DashboardWidgetType.COMMISSION_SUMMARY, name: 'Commission Summary', enabled: true, order: 14, minsize: 1 },
  { id: DashboardWidgetType.COMMISSION_BY_AGENT, name: 'Commission by Agent', enabled: true, order: 15, minsize: 2 },
  { id: DashboardWidgetType.COMMISSION_BY_MONTH, name: 'Commission by Month', enabled: true, order: 16, minsize: 2 },
  { id: DashboardWidgetType.COMMISSION_BY_STAGE, name: 'Commission by Stage', enabled: true, order: 17, minsize: 2 },
  { id: DashboardWidgetType.COMMISSION_BY_STATUS, name: 'Commission by Status', enabled: true, order: 18, minsize: 2 },
  { id: DashboardWidgetType.OFFICE_VS_AGENT, name: 'Office vs Agent Breakdown', enabled: true, order: 19, minsize: 2 },
  { id: DashboardWidgetType.INDUSTRY_SUMMARY, name: 'Industry Summary', enabled: true, order: 20, minsize: 1 },
  { id: DashboardWidgetType.TOTAL_BUSINESSES_CHART, name: 'Total Businesses Chart', enabled: true, order: 21, minsize: 2 },
  { id: DashboardWidgetType.TOTAL_PRICE_CHART, name: 'Total Price Chart', enabled: true, order: 22, minsize: 2 },
  { id: DashboardWidgetType.AVERAGE_PRICE_CHART, name: 'Average Price Chart', enabled: true, order: 23, minsize: 2 },
  { id: DashboardWidgetType.AVERAGE_MULTIPLE_CHART, name: 'Average Multiple Chart', enabled: false, order: 24, minsize: 2 },
]

export function useDashboardLayout() {
  const widgets = ref<DashboardWidget[]>([])
  const loading = ref(false)
  const saving = ref(false)

  const getDashboardLayout = async () => {
    loading.value = true
    try {
      const res = await $fetch('/api/v1/user-workspace/dashboard-layout', {
        method: 'GET',
      })
      return res
    }
    catch (error) {
      console.error('Failed to get dashboard layout:', error)
      throw error
    }
    finally {
      loading.value = false
    }
  }

  const saveDashboardLayout = async (layout: DashboardRow[]) => {
    saving.value = true
    try {
      const res = await $fetch('/api/v1/user-workspace/dashboard-layout', {
        method: 'PUT',
        body: { layout },
      })
      return res
    }
    catch (error) {
      console.error('Failed to save dashboard layout:', error)
      throw error
    }
    finally {
      saving.value = false
    }
  }


  const toggleWidget = (widgetId: string) => {
    const widget = widgets.value.find(w => w.id === widgetId)
    if (widget) {
      widget.enabled = !widget.enabled
    }
  }

  const updateWidgetOrder = (newOrder: DashboardWidget[]) => {
    widgets.value = newOrder.map((widget, index) => ({
      ...widget,
      order: index + 1,
    }))
  }

  const getEnabledWidgets = computed(() => {
    return widgets.value
      .filter(w => w.enabled)
      .sort((a, b) => a.order - b.order)
  })

  const getWidgetByName = (name: string) => {
    return widgets.value.find(w => w.name === name)
  }

  const resetLayout = () => {
    widgets.value = []
  }

  return {
    widgets,
    getEnabledWidgets,
    toggleWidget,
    updateWidgetOrder,
    getWidgetByName,
    resetLayout,
    getDashboardLayout,
    saveDashboardLayout,
    loading,
    saving,
  }
}

